import React, { useMemo, useState, useRef, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { DebouncedInput } from '../DebouncedInput';

const sharedInputStyles = css`
  border: 1px solid #ced4da;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  border-radius: 0.25rem;
  padding: 0.375rem 0.75rem;
  box-sizing: border-box;
  height: 2rem;
  background-color: #fff;
  color: #212529;
  flex-grow: 1;

  &:focus {
    outline: none;
    border-color: #86b7fe;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  padding: 0 0.25rem 0 0;
  margin-bottom: 0.25rem;
`;

const RangeContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  width: 100%;
  align-items: center;
`;

const StyledDebouncedInput = styled(DebouncedInput)`
  ${sharedInputStyles}
  width: ${({ width }) => width || 'auto'};
`;

const StyledSelect = styled.select`
  ${sharedInputStyles}
  width: 100%;
`;

const MultiSelectContainer = styled.div`
  position: relative;
  width: 100%;
`;

const MultiSelectButton = styled.button`
  ${sharedInputStyles}
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  text-align: left;

  &:hover {
    background-color: #f8f9fa;
  }
`;

const MultiSelectDropdown = styled.div`
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-height: 250px;
  overflow-y: auto;
  z-index: 1000;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: none;
  border-bottom: 1px solid #ced4da;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-bottom-color: #86b7fe;
  }
`;

const OptionItem = styled.label`
  display: flex;
  align-items: center;
  padding: 0.5rem;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover {
    background-color: #f8f9fa;
  }
`;

const Checkbox = styled.input`
  margin-right: 0.5rem;
  cursor: pointer;
`;

const SelectAllButton = styled.button`
  width: 100%;
  padding: 0.5rem;
  border: none;
  border-bottom: 1px solid #ced4da;
  background-color: #f8f9fa;
  cursor: pointer;
  text-align: left;
  font-size: 0.875rem;

  &:hover {
    background-color: #e9ecef;
  }
`;

// MultiSelect Component
const MultiSelect = ({
  options,
  value = [],
  onChange,
  placeholder = 'Selecionar...'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedValues = Array.isArray(value) ? value : [];

  const filteredOptions = options.filter((option) =>
    String(option).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleToggle = (option) => {
    const newValue = selectedValues.includes(option)
      ? selectedValues.filter((v) => v !== option)
      : [...selectedValues, option];
    onChange(newValue);
  };

  const handleSelectAll = () => {
    if (selectedValues.length === filteredOptions.length) {
      onChange([]);
    } else {
      onChange(filteredOptions);
    }
  };

  const displayText =
    selectedValues.length === 0
      ? placeholder
      : selectedValues.length === 1
      ? selectedValues[0]
      : `${selectedValues.length} selecionados`;

  return (
    <MultiSelectContainer ref={containerRef}>
      <MultiSelectButton type="button" onClick={() => setIsOpen(!isOpen)}>
        <span style={{ fontSize: '.7rem' }}>{displayText}</span>
        <span style={{ marginLeft: '0.5rem' }}>{isOpen ? '▲' : '▼'}</span>
      </MultiSelectButton>

      {isOpen && (
        <MultiSelectDropdown>
          <SearchInput
            type="text"
            placeholder="Buscar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />

          <SelectAllButton onClick={handleSelectAll}>
            {selectedValues.length === filteredOptions.length ? '☑' : '☐'}
            <span className="ml-2">Selecionar todos</span>
          </SelectAllButton>

          {filteredOptions.map((option) => (
            <OptionItem key={option}>
              <Checkbox
                type="checkbox"
                checked={selectedValues.includes(option)}
                onChange={() => handleToggle(option)}
              />
              {option}
            </OptionItem>
          ))}

          {filteredOptions.length === 0 && (
            <div
              style={{ padding: '1rem', textAlign: 'center', color: '#6c757d' }}
            >
              Nenhuma opção encontrada
            </div>
          )}
        </MultiSelectDropdown>
      )}
    </MultiSelectContainer>
  );
};

// Filter Component
export const Filter = ({ column }) => {
  const { filterVariant, options } = column.columnDef.meta || {};
  const columnFilterValue = column.getFilterValue();

  const sortedUniqueValues = useMemo(
    () =>
      filterVariant === 'range' || filterVariant === 'dateRange'
        ? []
        : Array.from(column.getFacetedUniqueValues().keys()).sort(),
    [column, filterVariant]
  );

  if (filterVariant === 'range') {
    const min = column.getFacetedMinMaxValues()?.[0];
    const max = column.getFacetedMinMaxValues()?.[1];

    return (
      <FilterContainer>
        <RangeContainer>
          <StyledDebouncedInput
            type="number"
            min={Number(min ?? '')}
            max={Number(max ?? '')}
            value={columnFilterValue?.[0] ?? ''}
            onChange={(value) =>
              column.setFilterValue((old) => [value, old?.[1]])
            }
            placeholder={`Min ${min !== undefined ? `(${min})` : ''}`}
          />

          <StyledDebouncedInput
            type="number"
            min={Number(min ?? '')}
            max={Number(max ?? '')}
            value={columnFilterValue?.[1] ?? ''}
            onChange={(value) =>
              column.setFilterValue((old) => [old?.[0], value])
            }
            placeholder={`Max ${max !== undefined ? `(${max})` : ''}`}
          />
        </RangeContainer>
      </FilterContainer>
    );
  }

  if (filterVariant === 'dateRange') {
    return (
      <FilterContainer>
        <RangeContainer>
          <StyledDebouncedInput
            type="date"
            value={columnFilterValue?.[0] ?? ''}
            onChange={(value) =>
              column.setFilterValue((old) => [value, old?.[1]])
            }
          />

          <StyledDebouncedInput
            type="date"
            value={columnFilterValue?.[1] ?? ''}
            onChange={(value) =>
              column.setFilterValue((old) => [old?.[0], value])
            }
          />
        </RangeContainer>
      </FilterContainer>
    );
  }

  if (filterVariant === 'multiselect') {
    const multiSelectOptions = options || sortedUniqueValues;

    return (
      <FilterContainer>
        <MultiSelect
          options={multiSelectOptions}
          value={columnFilterValue || []}
          onChange={(value) =>
            column.setFilterValue(value.length > 0 ? value : undefined)
          }
          placeholder="Selecionar..."
        />
      </FilterContainer>
    );
  }

  if (filterVariant === 'select') {
    const selectOptions = options || sortedUniqueValues;

    return (
      <FilterContainer>
        <StyledSelect
          onChange={(e) => column.setFilterValue(e.target.value)}
          value={columnFilterValue ?? ''}
        >
          <option value="">Todos</option>
          {selectOptions.map((value) => (
            <option value={value} key={value}>
              {value}
            </option>
          ))}
        </StyledSelect>
      </FilterContainer>
    );
  }

  const dataListOptions = options || sortedUniqueValues;

  return (
    <FilterContainer>
      <datalist id={column.id + 'list'}>
        {dataListOptions.map((value) => (
          <option value={value} key={value} />
        ))}
      </datalist>

      <StyledDebouncedInput
        type="text"
        value={columnFilterValue ?? ''}
        onChange={(value) => column.setFilterValue(value)}
        placeholder={`Buscar... (${column.getFacetedUniqueValues().size})`}
        list={column.id + 'list'}
      />
    </FilterContainer>
  );
};
